import { Button, InlineNotification, Tag } from '@carbon/react';
import { Download } from '@carbon/icons-react';
import { asProblem, downloadCsv, PageHeader, problemMessage, type CsvCell } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import type { ReactNode } from 'react';
import { ReportFilters } from './reportFilters';
import type { ReportRange } from './reportRange';

/**
 * The frame every HO report shares (DESIGN_SYSTEM.md §13.2a): the header with **Download CSV** (always there, disabled until
 * there is something to download) and the report's own actions after it, the filters, an error, then the report.
 *
 * `csvName` is the file's English stem (`sales`, `voids-discounts`); the period is added to it.
 */
export function ReportFrame({
  title,
  description,
  range,
  onRange,
  filters,
  csvName,
  csv,
  actions,
  error,
  children,
}: {
  title: string;
  description: string;
  range: ReportRange;
  onRange(range: ReportRange): void;
  filters?: ReactNode;
  csvName: string;
  /** What the CSV holds; leave it out while there is nothing to download. */
  csv?: () => CsvCell[][];
  actions?: ReactNode;
  error: unknown;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="pos-admin-page pos-report">
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            <Button
              kind="ghost"
              renderIcon={Download}
              disabled={!csv}
              onClick={() => csv && downloadCsv(`${csvName}-${range.from}-${range.to}.csv`, csv())}
            >
              {t('reports.downloadCsv')}
            </Button>
            {actions}
          </>
        }
      />
      <ReportFilters range={range} onChange={onRange}>
        {filters}
      </ReportFilters>
      {Boolean(error) && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(error))} />
      )}
      {children}
    </section>
  );
}

/** A change against the compared period, as a Carbon tag: green up, red down. */
export function Change({ now, before }: { now: number; before: number | undefined }) {
  const { t } = useTranslation();
  if (before === undefined) return null;
  if (before === 0) return now === 0 ? null : <Tag type="gray">{t('reports.new')}</Tag>;
  const change = ((now - before) / Math.abs(before)) * 100;
  const text = `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  return <Tag type={change > 0 ? 'green' : change < 0 ? 'red' : 'gray'}>{text}</Tag>;
}
